import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut, user, sendPasswordResetEmail } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Observable, switchMap, of } from 'rxjs';
import { Firestore, doc, getDoc, updateDoc, collection, getDocs, query, where } from '@angular/fire/firestore';
import { docData } from '@angular/fire/firestore';

interface User {
  uid: string;
  email: string | null;
  firstName?: string;
  lastName?: string;
  status?: 'active' | 'inactive';
  accountActivated?: boolean;
  lastLogin?: Date;
  role?: string;
  roles?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user$: Observable<any>;

  constructor(
    private auth: Auth,
    private router: Router,
    private firestore: Firestore
  ) {
    // Merge Firebase auth user with Firestore admin data
    this.user$ = user(this.auth).pipe(
      switchMap(user => {
        if (!user) return of(null);
        
        // Check admin collection first
        return docData(doc(this.firestore, `admins/${user.uid}`)).pipe(
          switchMap(adminData => {
            if (adminData) {
              // If user is in admins collection, merge auth and admin data
              return of({
                ...user,
                ...adminData
              });
            }
            
            // If not an admin, check regular users collection
            return docData(doc(this.firestore, `users/${user.uid}`)).pipe(
              switchMap(userData => {
                if (userData) {
                  return of({
                    ...user,
                    ...userData
                  });
                }
                return of(user);
              })
            );
          })
        );
      })
    );
  }

  async login(email: string, password: string): Promise<void> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const uid = userCredential.user.uid;

      // Check if user is an admin
      const adminDoc = await getDoc(doc(this.firestore, 'admins', uid));
      if (adminDoc.exists()) {
        // Update admin lastLogin
        await updateDoc(doc(this.firestore, 'admins', uid), {
          lastLogin: new Date()
        });
        this.router.navigate(['/admin']);
        return;
      }

      // If not admin, proceed with regular user login
      const userDoc = await getDoc(doc(this.firestore, 'users', uid));
      const userData = userDoc.data() as User | undefined;

      const updateData: Partial<User> = {
        status: 'active',
        lastLogin: new Date() // Always update lastLogin on successful login
      };

      if (!userData?.accountActivated) {
        updateData.accountActivated = true;
      }

      await updateDoc(doc(this.firestore, 'users', uid), updateData);
      this.router.navigate(['/dashboard']);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    this.router.navigate(['/']);
  }

  async resetPassword(email: string): Promise<void> {
    try {
      // Überprüfe zuerst, ob der Benutzer in unserer Datenbank existiert
      const usersQuery = query(collection(this.firestore, 'users'), where('email', '==', email));
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        throw new Error('Diese E-Mail-Adresse ist nicht registriert.');
      }

      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  isLoggedIn(): Observable<any> {
    return this.user$;
  }

  getCurrentUserId(): string | null {
    return this.auth.currentUser?.uid || null;
  }

  async getIdToken(): Promise<string | null> {
    if (this.auth.currentUser) {
      return await this.auth.currentUser.getIdToken();
    }
    return null;
  }
} 